import { useMemo } from 'react'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { GET_APPLICANT_METRICS } from 'src/graphql/applicants.graphql'

import {
  buildLaneCounts,
  buildLaneVariables,
  buildOutcomeVariables,
  buildProspectsFilterUrl,
  countFor,
  labelFor,
  laneTotal,
  monthWindow,
  OUTCOMES,
  PROSPECT_LANES,
} from '../lib/prospectCounts'

import ProspectsWidgetView from './ProspectsWidgetView'

/**
 * Data for the prospects strip. Everything it computes lives in
 * `../lib/prospectCounts` so the query variables and the drill-through URLs are
 * unit-tested — this file is only Apollo wiring, matching `AgendaWidget`.
 *
 * Five queries: one grouped over the four hiring stages, and one per outcome
 * because each outcome records when it happened on its own indexed column, and
 * `ApplicantFiltersInput` ANDs its date filters — so "hired this month OR
 * terminated this month" cannot be expressed in a single call.
 *
 * `GET_PIPELINE_SUMMARY` would collapse this to two queries, but it scopes
 * recruiters as `recruiterId` only while `applicantMetrics` and the Applicants
 * `?recruiters=` param both match lead-or-member — so its counts would disagree
 * with the list they open. Parity beats the round trip.
 *
 * Known gap: nothing invalidates these counts, so dispositioning an applicant
 * elsewhere leaves them stale until reload. `useApplicantDrawerContext()`'s
 * `refreshKey` is the existing signal if that becomes worth wiring.
 */
export interface ProspectsWidgetProps {
  /**
   * Whose prospects to count. Empty means no recruiter filter at all —
   * company-wide — which is what a Team scope needs.
   */
  recruiterIds?: string[]
}

const ProspectsWidget = ({ recruiterIds }: ProspectsWidgetProps) => {
  const { currentUser } = useAuth()

  // `active.employeeId` is the current structure; `employeeId` is the
  // deprecated flat field still present on older tokens.
  const myEmployeeId =
    currentUser?.active?.employeeId ?? currentUser?.employeeId

  const scope = useMemo(() => {
    if (recruiterIds) return recruiterIds.filter(Boolean)
    return myEmployeeId ? [myEmployeeId] : []
  }, [recruiterIds, myEmployeeId])

  // An explicitly empty array is Team scope, which is a real view. Only an
  // absent prop with no signed-in employee leaves us with nothing to ask about.
  const isScoped = Boolean(recruiterIds) || Boolean(myEmployeeId)

  // Recomputed per mount rather than per render, so the window cannot shift
  // between the lane query and the outcome queries.
  const thisMonth = useMemo(() => monthWindow(new Date()), [])

  const laneQuery = useQuery(GET_APPLICANT_METRICS, {
    variables: buildLaneVariables(scope),
    skip: !isScoped,
    notifyOnNetworkStatusChange: true,
  })

  // One useQuery per outcome. Hooks must not be called in a loop, so these are
  // written out — four is few enough that a loop would cost more than it saves.
  const hiredQuery = useQuery(GET_APPLICANT_METRICS, {
    variables: buildOutcomeVariables(OUTCOMES[0], scope, thisMonth),
    skip: !isScoped,
    notifyOnNetworkStatusChange: true,
  })
  const notInterestedQuery = useQuery(GET_APPLICANT_METRICS, {
    variables: buildOutcomeVariables(OUTCOMES[1], scope, thisMonth),
    skip: !isScoped,
    notifyOnNetworkStatusChange: true,
  })
  const disqualifiedQuery = useQuery(GET_APPLICANT_METRICS, {
    variables: buildOutcomeVariables(OUTCOMES[2], scope, thisMonth),
    skip: !isScoped,
    notifyOnNetworkStatusChange: true,
  })
  const terminatedQuery = useQuery(GET_APPLICANT_METRICS, {
    variables: buildOutcomeVariables(OUTCOMES[3], scope, thisMonth),
    skip: !isScoped,
    notifyOnNetworkStatusChange: true,
  })

  const outcomeQueries = [
    hiredQuery,
    notInterestedQuery,
    disqualifiedQuery,
    terminatedQuery,
  ]

  const lanes = useMemo(
    () => buildLaneCounts(laneQuery.data?.applicantMetrics?.items),
    [laneQuery.data]
  )

  const outcomes = useMemo(
    () =>
      OUTCOMES.map((outcome, index) => ({
        stage: outcome.stage,
        label: labelFor(outcome.stage),
        count: countFor(
          outcomeQueries[index].data?.applicantMetrics?.items,
          outcome.stage
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hiredQuery.data,
      notInterestedQuery.data,
      disqualifiedQuery.data,
      terminatedQuery.data,
    ]
  )

  const loading =
    laneQuery.loading || outcomeQueries.some((query) => query.loading)

  // One flag, not five toasts: this widget is read-only, so a toast per failed
  // query would stack up notifications the user can do nothing about.
  const hasError =
    Boolean(laneQuery.error) ||
    outcomeQueries.some((query) => Boolean(query.error))

  return (
    <ProspectsWidgetView
      lanes={lanes}
      outcomes={outcomes}
      total={laneTotal(lanes)}
      loading={loading}
      isScoped={isScoped}
      hasError={hasError}
      onOpenLane={(stage) => {
        const hiringStage = PROSPECT_LANES.find((lane) => lane === stage)
        if (!hiringStage) return
        navigate(buildProspectsFilterUrl({ kind: 'lane', hiringStage }, scope))
      }}
      onOpenOutcome={(stage) => {
        const outcome = OUTCOMES.find((entry) => entry.stage === stage)
        if (!outcome) return
        navigate(
          buildProspectsFilterUrl(
            { kind: 'outcome', outcome, window: thisMonth },
            scope
          )
        )
      }}
    />
  )
}

export default ProspectsWidget
